import { inject, signal } from '@angular/core';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';

/**
 * @description
 * Periodic refresh shared by the apps.
 *
 * While there is no connection the refresh is not lost: it is deferred and resolved with a single
 * refresh when the connection is back, so several outages in a row do not trigger several refreshes.
 * Each app decides what a full refresh means (which stores, and gated by which capabilities).
 *
 * It is shared because the deferral logic (the subtle part) used to be copy-pasted in the three apps.
 */
export abstract class BaseRefreshDataService {
  private readonly connectivity = inject(ConnectivityService);

  /** Pending periodic refresh, resolved once when the connection is back. */
  private readonly _isRefreshPending = signal(false);

  /** Periodic refresh entry point: performs it, or defers it when there is no connection. */
  public refreshAll(): void {
    if (this.connectivity.isOffline()) {
      this._isRefreshPending.set(true);
      return;
    }

    this._isRefreshPending.set(false);
    this.performRefreshAll();
  }

  /** Runs the refresh that was deferred while offline, if any. */
  public refreshPendingData(): void {
    if (!this._isRefreshPending() || this.connectivity.isOffline()) {
      return;
    }

    this._isRefreshPending.set(false);
    this.performRefreshAll();
  }

  /** What a full refresh means for this app. */
  protected abstract performRefreshAll(): void;
}
